// TODO: to rewrite in later tasks - ignore during CR for now

import { Route, Routes, useNavigate } from 'react-router-dom'
import NotFound from 'routes/NotFound'
import Page from 'components/global/Page'
import { useApp } from 'store/steps'
import { setCart } from 'store/cart-slice'
import {
    useCreateCartMutation,
    useGetCartQuery,
    useListCreatorCardPersonsQuery,
} from 'store/api-slice/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useEffect, useState } from 'react'
import FullPageLoader from 'components/global/FullPageLoader'
import ServerErrorModal from 'components/global/ServerErrorModal'
import steps from 'index'
import UnlimitedCalendarModal from 'components/global/CalendarModal/forks/UnlimitedCalendarModal'
import VouchersConfiguration from 'routes/vouchers/VouchersConfiguration'
import VouchersOrderAndPayment from 'routes/vouchers/VouchersOrderAndPayment'
import { setDeliveryType } from 'store/order-slice'
import { setIsAppAccessible, setIsErrorModalOpened } from 'store/general-slice'
import { useVouchers } from 'store/vouchers-slice/hooks'
import { restoreVouchersFromCart } from 'store/vouchers-slice'
import VouchersSummary from 'routes/vouchers/VouchersSummary'
import { pushDataLayer } from 'helpers/analytics'
import { CART_TYPE_VOUCHER } from 'constants/app'
import { vouchersFirstStep, vouchersSummaryStep } from 'routes/basenames'
import { STATUS_CODE_NO_SESSION } from 'constants/system'
import { catchErrorByStatus } from 'helpers/errors'
import ConfirmationModal from 'components/global/ConfirmationModal'
import { DEFAULT_LANG } from 'constants/i18n'
import { setIsCalendarModalOpened } from 'store/unlimited-slice'

const Vouchers = () => {
    const dispatch = useAppDispatch()
    const navigateTo = useNavigate()
    const { language, stepIndex, appMode, getPath } = useApp()
    const { removeVouchersFromCart, isProcessing } = useVouchers()

    const { cart } = useAppSelector((state) => state.cart)
    const { vouchersBoxes } = useAppSelector((state) => state.vouchers)
    const { isCalendarModalOpened } = useAppSelector((state) => state.unlimited)

    const [isRestoring, setIsRestoring] = useState(false)

    const {
        isFetching: isCartFetching,
        isError: isCartError,
        data: cartData,
        error: cartError,
    } = useGetCartQuery({
        _locale: language ?? DEFAULT_LANG,
        cartCreatorType: CART_TYPE_VOUCHER,
    })
    const [createCart, createCartStatus] = useCreateCartMutation()
    const { data: personsData, isFetching: arePersonsDataFetching } =
        useListCreatorCardPersonsQuery(
            {
                _locale: language ?? DEFAULT_LANG,
            },
            {
                skip: !isRestoring,
            }
        )

    const {
        isError: isCreateCartError,
        isSuccess: isCreateCartSuccess,
        data: createCartData,
    } = createCartStatus
    const isCartAlreadyCreated = cart?.cartId
    const isVerifiedUrl = stepIndex !== -1
    const isFirstStep =
        isVerifiedUrl && steps[stepIndex]?.id === 'vouchers_configuration'

    const handleAcceptError = () => {
        removeVouchersFromCart(true).then(() => {
            dispatch(setIsErrorModalOpened(false))
            // hard reload for cleanup in case of general error
            // > react router approach collides with steps logic
            window.location.replace(vouchersFirstStep)
        })
    }

    const handleCloseCalendar = () => {
        dispatch(setIsCalendarModalOpened(false))
    }

    useEffect(() => {
        if (isCartFetching || isCartAlreadyCreated) return

        // cart not exists
        if (isCartError && cartError) {
            if (
                catchErrorByStatus({
                    error: cartError,
                    ignored: [STATUS_CODE_NO_SESSION],
                })
            )
                return

            // not first step -> go to first step
            if (!isFirstStep && isVerifiedUrl) {
                navigateTo('/', { replace: true })
                return
            }

            // is first step -> create cart
            createCart({
                _locale: language ?? DEFAULT_LANG,
                cartCreatorType: CART_TYPE_VOUCHER,
            })
            return
        }

        // cart exists
        if (cartData) {
            const cartItems = cartData.cart?.items

            // cart exists, is empty, not first step -> go to first step
            if (
                cartItems &&
                cartItems.length === 0 &&
                !isFirstStep &&
                isVerifiedUrl
            ) {
                navigateTo('/', { replace: true })
                return
            }

            // cart exists, is not empty, vouchers state is empty
            if (cartItems && cartItems.length > 0 && !vouchersBoxes.length) {
                // is first step -> redirect to summary step
                if (isFirstStep && isVerifiedUrl) {
                    window.location.replace(vouchersSummaryStep)
                    return
                }
                // not first step -> rebuild vouchers state from cart
                setIsRestoring(true)
            }

            // set shipping delivery option when already in cart
            if (cartItems?.find((item) => item.productType === 'shipping')) {
                dispatch(setDeliveryType('shipping'))
            }

            // finally restore or just update cart state
            dispatch(setCart({ ...cartData, appMode }))
        }
    }, [isCartFetching])

    useEffect(() => {
        // fetch additional data and restore state based on cart - if needed
        if (!isRestoring || arePersonsDataFetching || !cartData) return

        // TODO: https://bitbucket.org/flyingbisons/pop-mercury-client/pull-requests/2629#comment-451500993
        const cartItems = cartData.cart?.items

        if (!cartItems || !personsData) return

        const { persons } = personsData

        if (!persons) return

        dispatch(
            restoreVouchersFromCart({
                cartVouchers: cartItems.filter(
                    (item) =>
                        item.productType === 'voucher' ||
                        item.productType === 'bundle'
                ),
                personsData: persons,
            })
        )
        setIsRestoring(false)
    }, [isRestoring, arePersonsDataFetching])

    useEffect(() => {
        isCreateCartError &&
            dispatch(setIsAppAccessible({ isAppAccessible: false }))
    }, [isCreateCartError])

    useEffect(() => {
        if (!isCreateCartSuccess) return

        dispatch(
            setCart({
                cart: { cartId: createCartData?.cartId },
                appMode,
                isFetchingLocked: true,
            })
        )
    }, [isCreateCartSuccess])

    useEffect(() => {
        // < analytics
        const storageRedirectSource = sessionStorage.getItem('vouchersSource')
        let redirectSource = 'direct'

        if (storageRedirectSource) {
            redirectSource = storageRedirectSource
            sessionStorage.removeItem('vouchersSource')
        }

        pushDataLayer({
            event: 'funnelStep',
            event_category: 'vouchers',
            event_action: 'vouchers_order_start',
            event_label: redirectSource,
        })
        // analytics >
    }, [])

    return !cart?.cartId ? (
        <FullPageLoader />
    ) : (
        <Page>
            <Routes>
                <Route path="/" element={<VouchersConfiguration />} />
                <Route
                    path={getPath('vouchers_summary')}
                    element={<VouchersSummary />}
                />
                <Route
                    path={getPath('vouchers_order')}
                    element={<VouchersOrderAndPayment />}
                />
                <Route path="*" element={<NotFound />} />
            </Routes>
            <UnlimitedCalendarModal
                isOpened={isCalendarModalOpened}
                onClose={handleCloseCalendar}
            />
            <ServerErrorModal
                onClick={handleAcceptError}
                isLoading={isProcessing}
            />
            <ConfirmationModal />
        </Page>
    )
}

export default Vouchers
